import React, { useState, useEffect } from 'react';
import { DataTable } from 'Components/Common/DataTable';
import { DataTableRow } from 'Components/Common/DataTable/DataTableRow';
import { TextCell } from 'Components/Common/DataTable/DataTableRow/TextCell';
import { DataTableBody } from 'Components/Common/DataTable/TableBody';
import { DataTableHeader } from 'Components/Common/DataTable/TableHeader';
import { DataTableHeaderItem } from 'Components/Common/DataTable/TableHeader/DataTableHeaderItem';
import { DataTableFooter } from 'Components/Common/DataTableFooter';
import { useDataGridEngine } from 'Components/Hooks/DataGridEngine';
import styles from './styles.module.scss';
import { config } from '../../../../../config';
import { ApplicableRatesInventoryRecords, ApplicableRatesHistoryProps } from '../types';
import { ToastNotification } from 'Feature/CommonComponents/ContentComponents';
import { ApplicationStates } from 'Components/Enums/ApplicationStatus';
import i18n from 'i18next';

const ProvincialApplicableRatesHistoryDetails = ({
  fields,
  provinceId
}: ApplicableRatesHistoryProps) => {
  const [toastState, setToastState] = useState<ApplicationStates>(ApplicationStates.DEFAULT);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string>('');
  const [id, setId] = useState(provinceId);

  const onApiErrorCallback = (e: any) => {
    try {
      if (id) {
        setShowToast(true);
        setToastState(ApplicationStates.ERROR);
        throw new Error(e);
      }
    } catch (e) {
      // console.log(e)
    }
  };

  const {
    rowData,
    currentPage,
    itemsPerPage,
    onItemsPerPageChange,
    totalItems,
    totalPages,
    itemRangeFrom,
    itemRangeTo,
    isLoading: isInventoryLoading,
    onPagerClick,
    getTableAccessibilityText
  } = useDataGridEngine<ApplicableRatesInventoryRecords>({
    apiBaseUrl: `${config.claimApi.urls.applicableHistoryRates}/${id}`,
    onApiErrorCallback,
    loadDataOnInitialLoad: false,
    defaultItemsPerPage: 10
  });

  const onToastCloseHandler = () => {
    setShowToast(false);
  };

  useEffect(() => {
    setId(provinceId);
  }, [provinceId]);

  return (
    <div>
      <ToastNotification
        type={toastState}
        isActive={showToast}
        title={
          toastState === ApplicationStates.ERROR
            ? i18n.t('DefaultActions-SystemErrorToast-Action-Title')
            : i18n.t('DefaultActions-SystemSuccessToast-Action-Title')
        }
        content={{
          value: i18n.t('DefaultsInventoryTable-SystemErrorToast-Message')
        }}
        onCloseCallback={() => onToastCloseHandler()}
      />
      <div className={styles.dataTable}>
        <DataTable
          name="provincialApplicableRates"
          isLoading={isInventoryLoading}
          caption={{ value: getTableAccessibilityText() ?? '' }}
          scrollOnHorizontalOverflow
        >
          <DataTableHeader>
            <DataTableHeaderItem name="codeEffectiveDate" displayText={fields.effectiveDate} />
            <DataTableHeaderItem name="taxRealEstateRate" displayText={fields.realestateRate} />
            <DataTableHeaderItem
              name="goodsAndServiceTaxRealEstateRatePercent"
              displayText={fields.gstTaxOnRealestate}
            />
            <DataTableHeaderItem
              name="provincialSalesTaxRealEstateRatePercent"
              displayText={fields.pstTaxOnRealestate}
            />
            <DataTableHeaderItem
              name="harmonizedSalesTaxRealEstateRatePercent"
              displayText={fields.hstTaxonRealestate}
            />
            <DataTableHeaderItem
              name="realEstateCommissionRatePercent"
              displayText={fields.rEstateCommRatePaid}
            />
            <DataTableHeaderItem
              name="legalActionTimeOfDayCount"
              displayText={fields.legalActionTiming}
            />
            <DataTableHeaderItem
              name="propertyManagementFeeAmount"
              displayText={fields.propMgtFlatFee}
            />
            <DataTableHeaderItem name="holdingDayCount" displayText={fields.holdingDays} />
            <DataTableHeaderItem
              name="maximumEditTitleAmount"
              displayText={fields.maxEditForTitle}
            />
            <DataTableHeaderItem
              name="maximumEditAssignmentAmount"
              displayText={fields.maxEditForAssign}
            />
            <DataTableHeaderItem
              name="maximumEditDeficitAmount"
              displayText={fields.maxEditForDefSale}
            />
            <DataTableHeaderItem
              name="maximumAppraisalEditAmount"
              displayText={fields.maxAppraislEdit}
            />
            <DataTableHeaderItem
              name="maximumUtilityEditAmount"
              displayText={fields.maxUtilityMoEdit}
            />
          </DataTableHeader>
          <DataTableBody zebra>
            {rowData &&
              rowData.map((row: ApplicableRatesInventoryRecords, index: Number) => (
                <DataTableRow
                  name={row.applicableRatesID}
                  key={`record${index}`}
                  // onClick={() => onRowSelect(row)}
                >
                  <TextCell name="codeEffectiveDate" text={row.codeEffectiveDate ?? ''} />
                  <TextCell name="taxRealEstateRate" text={row.taxRealEstateRate ?? ''} />
                  <TextCell
                    name="goodsAndServiceTaxRealEstateRatePercent"
                    text={row.goodsAndServiceTaxRealEstateRatePercent ?? ''}
                  />
                  <TextCell
                    name="provincialSalesTaxRealEstateRatePercent"
                    text={row.provincialSalesTaxRealEstateRatePercent ?? ''}
                  />
                  <TextCell
                    name="harmonizedSalesTaxRealEstateRatePercent"
                    text={row.harmonizedSalesTaxRealEstateRatePercent ?? ''}
                  />
                  <TextCell
                    name="realEstateCommissionRatePercent"
                    text={row.realEstateCommissionRatePercent ?? ''}
                  />
                  <TextCell
                    name="legalActionTimeOfDayCount"
                    text={row.legalActionTimeOfDayCount ?? ''}
                  />
                  <TextCell
                    name="propertyManagementFeeAmount"
                    text={row.propertyManagementFeeAmount ?? ''}
                  />
                  <TextCell name="holdingDayCount" text={row.holdingDayCount ?? ''} />
                  <TextCell name="maximumEditTitleAmount" text={row.maximumEditTitleAmount ?? ''} />
                  <TextCell
                    name="maximumEditAssignmentAmount"
                    text={row.maximumEditAssignmentAmount ?? ''}
                  />
                  <TextCell
                    name="maximumEditDeficitAmount"
                    text={row.maximumEditDeficitAmount ?? ''}
                  />
                  <TextCell
                    name="maximumAppraisalEditAmount"
                    text={row.maximumAppraisalEditAmount ?? ''}
                  />
                  <TextCell
                    name="maximumUtilityEditAmount"
                    text={row.maximumUtilityEditAmount ?? ''}
                  />
                </DataTableRow>
              ))}
          </DataTableBody>
        </DataTable>
        <div className={`${styles.dataTableFooter} row col-12 justify-content-end`}>
          <DataTableFooter
            name="manageUsersList"
            currentPage={currentPage}
            totalPages={totalPages || 1}
            itemsPerPageLabel={{
              value: i18n.t('DefaultsInventoryTable-ResultsPerPageDropdown-Title')
            }}
            itemsXofYText={`${itemRangeFrom || 0} - ${itemRangeTo || 0} ${i18n.t(
              'Globals-Grid-ResultsPerPageDropdown-Of'
            )}`}
            totalItems={totalItems}
            pagerClickCallback={onPagerClick}
            itemsPerPageOnChangeCallback={onItemsPerPageChange}
            itemsPerPageValue={itemsPerPage.toString()}
          />
        </div>
      </div>
    </div>
  );
};

export default ProvincialApplicableRatesHistoryDetails;
